import {doc, getDoc, getFirestore} from "firebase/firestore";
import AbstractDocument from "@/services/firebase/firestore/documents/AbstractDocument";
import {DocumentTypes} from "@/services/firebase/firestore/documents/DocumentTypes";
import {plainToInstance} from "class-transformer";
import DocumentOthers from "@/services/firebase/firestore/documents/DocumentOthers";
import humps from "lodash-humps-ts";
import DocumentPurchaseOperationTimers from "@/services/firebase/firestore/documents/DocumentPurchaseOperationTimers";
import FirestoreParser from "@/services/firebase/firestore/parsers/FirestoreParser";
import DocumentWithdrawalOperationTimers
    from "@/services/firebase/firestore/documents/DocumentWithdrawalOperationTimers";
import DocumentPurchase from "@/services/firebase/firestore/documents/DocumentPurchase";
import DocumentWithdrawal from "@/services/firebase/firestore/documents/DocumentWithdrawal";
import DocumentWarningBlocks from "@/services/firebase/firestore/documents/DocumentWarningBlocks";
import {ServiceWarningBlocks} from "@/services/v2/service-warning-blocks/ServiceWarningBlocks";
import DocumentFAQButtons from "@/services/firebase/firestore/documents/DocumentFAQButtons";
import {ServiceFAQButtons} from "@/services/v2/service-faq-buttons/ServiceFAQButtons";

type DocumentVersionTypes = Exclude<DocumentTypes, DocumentTypes.WITHDRAWAL_OPERATION_TIMERS | DocumentTypes.PURCHASE_OPERATION_TIMERS | DocumentTypes.UNKNOWN>;

export default class FirebaseServiceFirestore {
    private readonly _firestore: any = null;
    private _VERSION_DATABASE_PATH = `mobile/${import.meta.env.VITE_APP_TYPE}-version/${import.meta.env.VITE_APP_PROJECT}`;
    private _GLOBAL_DATABASE_PATH = `mobile/global-settings`;
    private _documents: Map<string, AbstractDocument> = new Map();
    private _parser: FirestoreParser = new FirestoreParser(this);
    private readonly _documentVersionTypeMap: Record<DocumentVersionTypes, { new (): AbstractDocument }> = {
        [DocumentTypes.OTHERS]: DocumentOthers,
        [DocumentTypes.PURCHASE]: DocumentPurchase,
        [DocumentTypes.WITHDRAWAL]: DocumentWithdrawal,
        [DocumentTypes.WARNING_BLOCKS]: DocumentWarningBlocks,
        [DocumentTypes.FAQ_BUTTONS]: DocumentFAQButtons,
    }

    public constructor(app: any) {
        this._firestore = getFirestore(app);
    }

    public async fetchVersionDatabaseDocuments() {
        const documents = Object.entries(this._documentVersionTypeMap) as [DocumentVersionTypes, { new (): AbstractDocument }][]
        await Promise.all(
            documents.map(async ([key, DocumentClass]) => {
                switch (key) {
                    case DocumentTypes.WARNING_BLOCKS:
                        await this.fetchWarningBlocksDocument();
                        break;
                    case DocumentTypes.FAQ_BUTTONS:
                        await this.fetchFAQButtonsDocument();
                        break;
                    default:
                        await this.fetchDocument(key, DocumentClass);
                        break;
                }
            })
        );
    }

    private async fetchDocument<T extends AbstractDocument>(key: DocumentTypes, DocumentClass: new () => T): Promise<T | null> {
        const collection = await getDoc(doc(this.firestore, this._VERSION_DATABASE_PATH, key));
        if (!collection.exists()) return null;

        const instance = plainToInstance(DocumentClass, humps(collection.data())) as T;

        this._documents.set(key, instance as T);
        return instance;
    }

    public async fetchPurchaseDocument() {
        return this.fetchDocument<DocumentPurchase>(DocumentTypes.PURCHASE, DocumentPurchase);
    }

    public async fetchWithdrawalDocument() {
        return this.fetchDocument<DocumentWithdrawal>(DocumentTypes.WITHDRAWAL, DocumentWithdrawal);
    }

    public async fetchWarningBlocksDocument() {
        const doc = await this.fetchDocument<DocumentWarningBlocks>(DocumentTypes.WARNING_BLOCKS, DocumentWarningBlocks);
        ServiceWarningBlocks.of().warningBlocks = doc;
        return doc;
    }

    public async fetchFAQButtonsDocument() {
        const doc = await this.fetchDocument<DocumentFAQButtons>(DocumentTypes.FAQ_BUTTONS, DocumentFAQButtons);
        ServiceFAQButtons.of().setDataDoc(doc);
        return doc;
    }

    public async fetchGlobalDatabaseDocuments() {
        const documents = [DocumentTypes.PURCHASE_OPERATION_TIMERS, DocumentTypes.WITHDRAWAL_OPERATION_TIMERS];
        await Promise.all(documents.map(async (el: string) => {
            const collection = await getDoc(doc(this.firestore, this._GLOBAL_DATABASE_PATH, el));
            if (!collection.exists()) return false;
            switch (el) {
                case DocumentTypes.PURCHASE_OPERATION_TIMERS.toString():
                    this._documents.set(el, plainToInstance(DocumentPurchaseOperationTimers, humps(collection.data())) as DocumentPurchaseOperationTimers);
                    break;
                case DocumentTypes.WITHDRAWAL_OPERATION_TIMERS.toString():
                    this._documents.set(el, plainToInstance(DocumentWithdrawalOperationTimers, humps(collection.data())) as DocumentWithdrawalOperationTimers);
                    break;
                default:
                    return false;
            }
        }))
    }

    get documents(): Map<string, AbstractDocument> {
        return this._documents;
    }

    public getDocument<T extends AbstractDocument>(key: DocumentTypes): T | null {
        const document = this._documents.get(key.toString());

        return document as T || null;
    }

    get parser(): FirestoreParser {
        return this._parser;
    }

    get firestore(): any {
        return this._firestore;
    }
}
